// /groups/:groupId/fasting — Phase 1 of docs/plan-fasting-groups.md.
//
// ONE screen with three parts, in the order somebody standing in their kitchen
// at hour fifteen wants them: their own dial, the control to log a ketosis
// reading, and everybody else's dials side by side.
//
// ⚠ THE PAGE TICKS ITSELF, and that is the whole reason this feature needs no
// realtime. Every dial is derived from a stored `startedAt` and `Date.now()`, so
// a local interval advances all of them with no server round trip. The only
// server-side changes are somebody STARTING or ENDING a fast, which happen once
// or twice a day, and a slow poll covers that. Compare tournaments, where a
// re-seated round has to reach a room in seconds and earned a WebSocket frame.
//
// The tick and the poll are deliberately different intervals: the clock has to
// look alive (30s, which is enough for a minutes-resolution readout) and the
// fetch does not (90s).

import {
  ApiClientError,
  endFast,
  fetchFasting,
  fetchGroup,
  logKetosis,
  startFast,
  updateFast,
} from '../api/client.js';
import { navigate, onLeave, type View } from '../router.js';
import { el } from '../dom.js';
import { groupShowsLeaderboard, groupSubnav } from './groupNav.js';
import { fastDialFigure } from './fastDial.js';
import {
  FAST_END_LABELS,
  FAST_END_REASONS,
  FAST_TARGET_PRESETS,
  GroupRole,
  KETOSIS_LABELS,
  KETOSIS_LEVELS,
  formatElapsed,
  type FastEndReason,
  type FastingState,
  type KetosisLevel,
} from '@msc/shared';

/** How often the dials redraw. Minutes-resolution readout, so 30s is plenty. */
const TICK_MS = 30_000;
/** How often the state is re-fetched. Somebody else starting a fast is rare. */
const POLL_MS = 90_000;

export const fastingView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  let state: FastingState | null = null;
  // ⚠ Fetched ONCE, outside load(), because load() is the 90s poll and your
  // role in the group does not change on that cadence. Its only job today is
  // the Manage tab. A failure must not block the dials, so it is deliberately
  // NOT awaited alongside the fasting state -- see the empty catch, and note
  // render() is safe to call from either order because whichever resolves
  // second re-renders.
  //
  // The response also carries `group.name`, which this page still does not
  // show anywhere -- it is the one screen inside a group that never names the
  // group. See docs/todo-mac-hand-test-2026-09-10.md 10j.
  let canManage = false;
  let showsBoard = true;
  let tick: number | undefined;
  let poll: number | undefined;
  onLeave(() => {
    window.clearInterval(tick);
    window.clearInterval(poll);
  });

  void (async () => {
    try {
      const group = await fetchGroup(groupId);
      canManage = group.myRole >= GroupRole.ADMIN;
      showsBoard = groupShowsLeaderboard(group.allowedEventTypes);
      if (state) render();
    } catch {
      // Nav furniture only. A member who cannot read the group is already
      // handled by the fasting fetch's own 403 branch.
    }
  })();

  async function load(): Promise<void> {
    try {
      state = await fetchFasting(groupId);
      render();
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate('/login', true);
        return;
      }
      if (err instanceof ApiClientError && (err.status === 403 || err.status === 404)) {
        main.replaceChildren(
          el('p', { class: 'form-error' }, 'You are not a member of this group.'),
        );
        return;
      }
      // ⚠ Only when nothing has rendered yet. A failed poll must not replace a
      // page of live dials with an error -- the clocks are all derived locally
      // and keep being right, so the honest thing is to leave them alone and
      // let the next poll recover. Same rule as the tournament board.
      if (state === null) {
        main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load the fasts.'));
      }
    }
  }

  /** Re-render from unchanged state, so the clocks move. */
  function retick(): void {
    if (state) render();
  }

  function render(): void {
    const s = state;
    if (!s) return;

    window.clearInterval(tick);
    window.clearInterval(poll);
    tick = window.setInterval(retick, TICK_MS);
    poll = window.setInterval(() => void load(), POLL_MS);

    main.replaceChildren(
      // ⚠ `showFasting` must be TRUE here, and the first draft omitted it -- so
      // the nav rendered without the tab that was currently active, which reads
      // as having navigated somewhere outside the group. Being ON this page is
      // proof enough that the group runs fasts; the flag exists to decide
      // whether OTHER screens offer the link.
      //
      // ⚠ `canManage` was hardcoded FALSE here until 2026-09-10, in the same
      // call as that comment -- so an owner standing on this page lost the
      // Manage tab and could not tell it was this screen that had dropped it.
      // This view is the only one inside a group that does not fetch the group,
      // which is exactly why it was the one that got it wrong.
      groupSubnav(groupId, 'fasting', canManage, true, showsBoard),
      el('div', { class: 'page-head' }, [
        el('h1', {}, 'Fasting'),
        el('p', { class: 'muted' }, statusLine(s)),
        el('p', { class: 'small' }, [el('a', { href: `/groups/${groupId}/fasting/history` }, 'Fasting history')]),
      ]),
      ...(s.groupFast ? [groupFastCard(s)] : []),
      s.mine ? myFastCard(s) : startCard(s),
      ketosisCard(s),
      othersCard(s),
      el('p', { class: 'small' }, [
        el('a', { href: '/fasting/learn' }, 'How fasting works'),
      ]),
    );
  }

  function statusLine(s: FastingState): string {
    const running = s.others.length + (s.mine ? 1 : 0);
    if (running === 0) return 'Nobody is fasting right now.';
    if (running === 1) return s.mine ? 'You are fasting.' : '1 person is fasting.';
    return `${running} people are fasting.`;
  }

  /** The shared group fast, when the group has one on the calendar. */
  function groupFastCard(s: FastingState): HTMLElement {
    const gf = s.groupFast;
    if (!gf) return el('div', {});
    return el('section', { class: 'card' }, [
      el('h2', {}, 'The group fast'),
      el('p', {}, [el('a', { href: `/events/${gf.eventId}` }, gf.title)]),
      el(
        'p',
        { class: 'muted small' },
        // Explains the model rather than assuming it: the event is a target,
        // not a switch that starts everybody's clock at once.
        'Everyone starts in their own time. The event is the target, and your own ' +
          'clock starts when you say it does.',
      ),
    ]);
  }

  // --- Starting ----------------------------------------------------------

  function startCard(s: FastingState): HTMLElement {
    const problem = el('p', { class: 'form-error', hidden: true });

    const target = el('select', { 'aria-label': 'Target length' }) as HTMLSelectElement;
    target.append(el('option', { value: '' }, 'No set target'));
    for (const preset of FAST_TARGET_PRESETS) {
      target.append(el('option', { value: String(preset.hours) }, preset.label));
    }
    // 16 hours is where the plan's progression starts, and the commonest thing
    // anybody picks. Defaulting to "no target" would make the dial's ring
    // meaningless on a first fast.
    target.value = '16';

    // ⚠ Offered, and defaulted to empty, because "I stopped eating at eight
    // last night" is the NORMAL way this gets logged rather than an edge case.
    // Empty means now, so the common path is still one tap.
    const startedAt = el('input', {
      type: 'datetime-local',
      'aria-label': 'When you actually started',
    }) as HTMLInputElement;

    const go = el('button', { class: 'btn', type: 'button' }, 'Start fasting');
    go.addEventListener('click', () => {
      go.setAttribute('disabled', 'disabled');
      problem.hidden = true;
      const body: Parameters<typeof startFast>[1] = {};
      if (target.value) body.targetHours = Number(target.value);
      if (startedAt.value) body.startedAt = localInputToUtc(startedAt.value);
      if (s.groupFast) body.eventId = s.groupFast.eventId;

      void startFast(groupId, body)
        .then((next) => {
          state = next;
          render();
        })
        .catch((err: unknown) => {
          // The server's message names the other group when a fast is already
          // running elsewhere, so it is shown verbatim.
          problem.textContent =
            err instanceof ApiClientError ? err.message : 'Could not start it.';
          problem.hidden = false;
          go.removeAttribute('disabled');
        });
    });

    return el('section', { class: 'card' }, [
      el('h2', {}, 'Start a fast'),
      el('label', { class: 'stack' }, ['Aiming for', target]),
      el('label', { class: 'stack' }, ['Started at (leave blank for now)', startedAt]),
      el('div', { class: 'walkin-row' }, [go]),
      problem,
    ]);
  }

  // --- My running fast ---------------------------------------------------

  function myFastCard(s: FastingState): HTMLElement {
    const mine = s.mine;
    if (!mine) return el('div', {});
    const problem = el('p', { class: 'form-error', hidden: true });

    const act = (p: Promise<FastingState>, btn: HTMLElement) => {
      btn.setAttribute('disabled', 'disabled');
      problem.hidden = true;
      void p
        .then((next) => {
          state = next;
          render();
        })
        .catch((err: unknown) => {
          problem.textContent =
            err instanceof ApiClientError ? err.message : 'That did not work.';
          problem.hidden = false;
          btn.removeAttribute('disabled');
        });
    };

    const reason = el('select', { 'aria-label': 'Why you are stopping' }) as HTMLSelectElement;
    for (const key of FAST_END_REASONS) {
      reason.append(el('option', { value: key }, FAST_END_LABELS[key]));
    }
    const stop = el('button', { class: 'btn', type: 'button' }, 'End this fast');
    stop.addEventListener('click', () => {
      act(endFast(groupId, mine.sessionId, { reason: reason.value as FastEndReason }), stop);
    });

    const fix = el('input', {
      type: 'datetime-local',
      'aria-label': 'Correct the start time',
      value: utcToLocalInput(mine.startedAt),
    }) as HTMLInputElement;
    const save = el('button', { class: 'btn-link', type: 'button' }, 'Correct it');
    save.addEventListener('click', () => {
      if (!fix.value) return;
      act(updateFast(groupId, mine.sessionId, { startedAt: localInputToUtc(fix.value) }), save);
    });

    return el('section', { class: 'card' }, [
      fastDialFigure({ session: mine, nowMs: Date.now(), size: 'large' }),
      el('div', { class: 'walkin-row' }, [reason, stop]),
      el(
        'p',
        { class: 'muted small' },
        // The plan is explicit that no reason here is a failure, and the copy
        // has to say so rather than leaving the list to imply a verdict.
        'None of those is a failure. Recording why is what makes it useful to look ' +
          'back on.',
      ),
      el('details', {}, [
        el('summary', {}, 'Started earlier than this?'),
        el('div', { class: 'walkin-row' }, [fix, save]),
      ]),
      problem,
    ]);
  }

  // --- Ketosis -----------------------------------------------------------

  function ketosisCard(s: FastingState): HTMLElement {
    const problem = el('p', { class: 'form-error', hidden: true });
    const current = s.myKetosis[0]?.level;
    // ⚠ NOT three `.btn`s. Three equal-weight primaries read as three separate
    // actions and competed with "Start fasting" directly above -- which is this
    // page's real primary action in the state where nobody is fasting. It is
    // ONE CHOICE among three, so it gets the same control the RSVP row uses:
    // outlined, with the current answer filled. It also stops "Not in ketosis",
    // the leftmost, from looking like the button the page wants pressed.
    const buttons = KETOSIS_LEVELS.map((level) => {
      const chosen = level === current;
      const b = el(
        'button',
        {
          class: chosen ? 'btn-choice btn-choice-active' : 'btn-choice',
          type: 'button',
          // The fill is not the only signal: a reading is a real answer to a
          // question, so say which one is current to a screen reader too.
          'aria-pressed': chosen ? 'true' : 'false',
        },
        KETOSIS_LABELS[level].label,
      );
      b.addEventListener('click', () => {
        b.setAttribute('disabled', 'disabled');
        problem.hidden = true;
        void logKetosis(groupId, { level })
          .then((next) => {
            state = next;
            render();
          })
          .catch((err: unknown) => {
            problem.textContent =
              err instanceof ApiClientError ? err.message : 'Could not log it.';
            problem.hidden = false;
            b.removeAttribute('disabled');
          });
      });
      return b;
    });

    const latest = s.myKetosis[0];
    return el('section', { class: 'card' }, [
      el('h2', {}, 'Ketosis'),
      el(
        'p',
        { class: 'muted small' },
        // Three levels, and why. Somebody squinting at a strip against a printed
        // gradient is the actual situation this replaces.
        'Three levels rather than a strip colour, because a printed gradient is hard ' +
          'to read and people disagree with themselves. Log it any time: during the ' +
          'weeks before a fast as well as during one.',
      ),
      el(
        'p',
        { class: 'muted small' },
        // ⚠ THE MECHANIC WAS INVISIBLE. `starting_ketosis` is the last reading
        // at or BEFORE a fast starts, and it compresses the dial's stage bands
        // (shared/src/fasting.ts, KETOSIS_SPEEDUP) -- so a reading logged now
        // changes a dial that has not been started yet. Nothing on this page
        // said so, and in the state Will was testing there was no dial on
        // screen at all to connect it to. Without this line the only way to
        // learn that logging first matters is to read the source.
        'Log one BEFORE you start: the last reading from before a fast begins is what ' +
          'the timeline is adjusted by, because somebody already fat adapted does not ' +
          'spend twelve hours getting into ketosis. It is an approximation, not a ' +
          'measurement.',
      ),
      el('div', { class: 'walkin-row' }, buttons),
      ...(latest
        ? [
            el(
              'p',
              { class: 'muted small' },
              `Last logged: ${KETOSIS_LABELS[latest.level].label}, ${relativeUtc(latest.loggedAt)}.`,
            ),
          ]
        : []),
      ...(s.myKetosis.length > 1
        ? [
            el(
              'ul',
              { class: 'card-list' },
              s.myKetosis.slice(0, 8).map((r) =>
                el('li', {}, [
                  el('span', {}, KETOSIS_LABELS[r.level].label),
                  el('span', { class: 'muted small' }, ` ${relativeUtc(r.loggedAt)}`),
                ]),
              ),
            ),
          ]
        : []),
      problem,
    ]);
  }

  // --- Everybody else ----------------------------------------------------

  function othersCard(s: FastingState): HTMLElement {
    if (s.others.length === 0) {
      return el('section', { class: 'card' }, [
        el('h2', {}, 'The group'),
        el('p', { class: 'muted' }, 'Nobody else in this group is fasting right now.'),
      ]);
    }
    const now = Date.now();
    return el('section', { class: 'card' }, [
      el('h2', {}, `The group: ${s.others.length} fasting`),
      el(
        'div',
        { class: 'dial-grid' },
        s.others.map((session) => fastDialFigure({ session, nowMs: now, size: 'small' })),
      ),
    ]);
  }

  await load();
};

/**
 * A `datetime-local` value to a UTC instant string.
 *
 * ⚠ `datetime-local` has NO zone, and its value is whatever the browser's own
 * clock calls that moment. So it is parsed as local and converted -- reading it
 * as UTC would shift every hand-entered start time by the viewer's offset,
 * which for anybody west of London means a fast that started last night reads
 * as starting this morning.
 */
function localInputToUtc(value: string): string {
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return value;
  return new Date(ms).toISOString().slice(0, 19).replace('T', ' ');
}

/** The reverse, for pre-filling the correction box. */
function utcToLocalInput(utc: string): string {
  const ms = Date.parse(`${utc.replace(' ', 'T')}Z`);
  if (!Number.isFinite(ms)) return '';
  const local = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/** "3h ago". Enough for a log list; the dial carries the precise number. */
function relativeUtc(utc: string): string {
  const ms = Date.parse(`${utc.replace(' ', 'T')}Z`);
  if (!Number.isFinite(ms)) return 'at an unknown time';
  const hours = (Date.now() - ms) / 3_600_000;
  if (hours < 0.02) return 'just now';
  if (hours < 24) return `${formatElapsed(hours)} ago`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}
